import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';
import PermissionModal from "./partials/modals/PermissionModal";

import {
    getRolePermissions as onGetRolePermissions,
    getRoles as onGetRoles,
    getFeatures as onGetFeatures,
    getActions as onGetActions,
    addNewRolePermission as onAddNewRolePermission,
    updateRolePermission as onUpdateRolePermission,
    deleteRolePermission as onDeleteRolePermission,
} from "store/actions";

import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import {
    Col,
    Row,
    UncontrolledTooltip,
    Card,
    CardBody,
    Button,
} from "reactstrap";

import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";

const RolePermission = () => {

    document.title = "Droits d'utilisateurs | CinzanEdu";

    const dispatch = useDispatch();

    // ------------------------------
    //     MODAL STATE
    // ------------------------------
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [rolePermission, setRolePermission] = useState(null);

    // ------------------------------
    //     SELECTORS
    // ------------------------------
    const RolePermissionProperties = createSelector(
        (state) => state.rolePermissionReducer,
        (rolePermissionReducer) => ({
            rolePermissions: rolePermissionReducer.rolePermissions,
            loading: rolePermissionReducer.loading
        })
    );
    const { rolePermissions, loading } = useSelector(RolePermissionProperties);

    const RoleProperties = createSelector(
        (state) => state.roleReducer,
        (roleReducer) => ({
            roles: roleReducer.roles,
        })
    );
    const { roles } = useSelector(RoleProperties);

    const FeatureProperties = createSelector(
        (state) => state.featureReducer,
        (featureReducer) => ({
            features: featureReducer.features,
        })
    );
    const { features } = useSelector(FeatureProperties);

    const ActionProperties = createSelector(
        (state) => state.actionReducer,
        (actionReducer) => ({
            actions: actionReducer.actions,
        })
    );
    const { actions } = useSelector(ActionProperties);

    // ------------------------------
    //     LOAD DATA
    // ------------------------------
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (rolePermissions && !rolePermissions.length) dispatch(onGetRolePermissions());
        if (roles && !roles.length) dispatch(onGetRoles());
        if (features && !features.length) dispatch(onGetFeatures());
        if (actions && !actions.length) dispatch(onGetActions());
    }, [dispatch, rolePermissions, roles, features, actions]);

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    // ------------------------------
    //      MODAL TOGGLE
    // ------------------------------
    const toggle = () => {
        if (modal) {
            setModal(false);
            setRolePermission(null);
            setIsEdit(false);
        } else {
            setModal(true);
        }
    };

    // ------------------------------
    //      ADD NEW
    // ------------------------------
    const onClickNewRolePermission = () => {
        setIsEdit(false);
        setRolePermission(null);
        toggle();
    };

    // ------------------------------
    //      EDIT
    // ------------------------------

    
    const handleRolePermissionClick = (rolePerm) => {
        setIsEdit(true);

        // Préparer les actions sélectionnées pour react-select
        let initialSelectedActions = [];

        if (rolePerm.permission) {
            // Cas multiple actions via feature_permissions
            if (rolePerm.permission.feature_permissions && Array.isArray(rolePerm.permission.feature_permissions)) {
                initialSelectedActions = rolePerm.permission.feature_permissions.map(a => ({
                    value: a.id,
                    label: a.name + (a.code ? ` (${a.code})` : "")
                }));
            } else if (rolePerm.permission.action) {
                // Cas action unique
                initialSelectedActions = [{
                    value: rolePerm.permission.action.id,
                    label: rolePerm.permission.action.name + (rolePerm.permission.action.code ? ` (${rolePerm.permission.action.code})` : "")
                }];
            }
        }

        setRolePermission({
            ...rolePerm,
            initialSelectedActions
        });

        toggle();
    };

    // ------------------------------
    //      DELETE
    // ------------------------------
    const [deleteModal, setDeleteModal] = useState(false);

    const onClickDelete = (perm) => {
        setRolePermission(perm);
        setDeleteModal(true);
    };

    const handleDeleteRolePermission = () => {
        if (rolePermission?.uid) {
            dispatch(onDeleteRolePermission(rolePermission.uid));
            setDeleteModal(false);
        }
    };

    // ------------------------------
    //     SAVE FROM MODAL
    // ------------------------------
    const handleSave = (payload) => {
        if (!payload.role_id) return; // sécurité

        if (isEdit) {
            dispatch(onUpdateRolePermission(payload)); // payload = { role_id, permissions }
        } else {
            dispatch(onAddNewRolePermission(payload));
        }

        toggle();
    };

    // ------------------------------
    //     TABLE COLUMNS
    // ------------------------------
    const columns = useMemo(() => [
        {
            header: 'ID',
            accessorKey: "id",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cellProps) => (
                <Link to="#" className="text-body fw-bold">{cellProps.row.original.id}</Link>
            )
        },
        {
            header: "Role",
            accessorKey: "role.name",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Fonctionnalité",
            accessorKey: "permission.feature.name",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Action",
            accessorKey: "permission.action.name",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Code",
            accessorKey: "permission.code",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Action',
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cellProps) => (
                <ul className="list-unstyled hstack gap-1 mb-0">
                    <li>
                        <Link
                            to="#"
                            className="btn btn-sm btn-soft-info"
                            onClick={() => handleRolePermissionClick(cellProps.row.original)}
                            id={`edittooltip-${cellProps.row.original.id}`}
                        >
                            <i className="mdi mdi-pencil-outline" />
                            <UncontrolledTooltip placement="top" target={`edittooltip-${cellProps.row.original.id}`} >
                                Edit
                            </UncontrolledTooltip>
                        </Link>
                    </li>
                    <li>
                        <Link
                            to="#"
                            className="btn btn-sm btn-soft-danger"
                            onClick={() => onClickDelete(cellProps.row.original)}
                            id={`deletetooltip-${cellProps.row.original.id}`}
                        >
                            <i className="mdi mdi-delete-outline" />
                            <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                                Delete
                            </UncontrolledTooltip>
                        </Link>
                    </li>
                </ul>
            )
        },
    ], []);

    // ------------------------------
    //      RENDER
    // ------------------------------
    return (
        <React.Fragment>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeleteRolePermission}
                onCloseClick={() => setDeleteModal(false)}
            />

            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs title="Droits d'utilisateurs" breadcrumbItem="Liste des droits d'utilisateurs" />

                    {isLoading ? (
                        <Spinners setLoading={setIsLoading} /> 
                    ) : (
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardBody className="border-bottom">
                                        <div className="d-flex align-items-center">
                                            <h5 className="mb-0 card-title flex-grow-1"></h5>
                                            <div className="flex-shrink-0">
                                                <Link to="#!" onClick={onClickNewRolePermission} className="btn btn-primary">
                                                    Ajouter un droit
                                                </Link>
                                            </div>
                                        </div>
                                    </CardBody>

                                    <CardBody>
                                        <TableContainer
                                            columns={columns}
                                            data={rolePermissions || []}
                                            isCustomPageSize={true}
                                            isGlobalFilter={true}
                                            isPagination={true}
                                            SearchPlaceholder="Rechercher ..."
                                            tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                                            theadClass="table-light"
                                            pagination="pagination"
                                            paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* MODAL PERMISSION */}
                    <PermissionModal
                        isOpen={modal}
                        toggle={toggle}
                        roles={roles}
                        features={features}
                        actions={actions}
                        onSave={handleSave}
                        initialRoleId={rolePermission?.role_id || ""}
                        initialPermissions={rolePermission?.initialSelectedActions || []}
                    />
                </div>
            </div>

            <ToastContainer />
        </React.Fragment>
    );
};

export default RolePermission;
